import type { FruitSliceFrameType } from '@fruitful/types';
import { router } from 'expo-router';
import { useState } from 'react';
import {
    BodyMediumRegular,
    CodeIcon,
    ExternalLinkIcon,
    FootnoteRegular,
    H5Emphasized,
    H6Emphasized,
    IconButton,
    Row,
    RowList,
    Spinner,
    Text,
    UsersIcon,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { useListFruitSliceListeners } from '../hooks/useFruitSliceListeners';
import {
    useGetFruitSlice,
    useListFruitSliceFrames,
} from '../hooks/useFruitSlices';
import { FruitSlicePermissionsModal } from './FruitSlicePermissionsModal';

export const FruitSliceManagementPage = ({ sliceId }: { sliceId: string }) => {
    const [permissionsOpen, setPermissionsOpen] = useState(false);
    const { data: slice, isPending: isLoadingSlice } =
        useGetFruitSlice(sliceId);
    const { data: frames = [], isPending: isLoadingFrames } =
        useListFruitSliceFrames(sliceId);
    const { data: listeners = [], isPending: isLoadingListeners } =
        useListFruitSliceListeners(sliceId);

    if (isLoadingSlice || isLoadingFrames || isLoadingListeners) {
        return (
            <YStack flex={1} alignItems="center" justifyContent="center">
                <Spinner />
            </YStack>
        );
    }

    if (slice == null) {
        return (
            <YStack padding="$6">
                <BodyMediumRegular>
                    Fruit Slice “{sliceId}” not found.
                </BodyMediumRegular>
            </YStack>
        );
    }

    const pageFrames = frames.filter((frame) => frame.type === 'PAGE');
    const solePage = pageFrames.length === 1 ? pageFrames[0] : undefined;

    return (
        <YStack
            width="100%"
            maxWidth={880}
            marginHorizontal="auto"
            padding="$5"
            gap="$5"
        >
            <YStack gap="$2">
                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    gap="$4"
                >
                    <XStack flex={1} alignItems="center" gap="$3">
                        <Text fontSize={32}>{slice.icon ?? '🍋'}</Text>
                        <H5Emphasized>{slice.name}</H5Emphasized>
                    </XStack>
                    <XStack alignItems="center" gap="$2">
                        {solePage != null && (
                            <Button
                                size="small"
                                icon={ExternalLinkIcon}
                                iconPosition="left"
                                href={{
                                    pathname: '/fruit-slices/[id]',
                                    params: {
                                        id: slice.id,
                                        frameId: solePage.id,
                                    },
                                }}
                            >
                                Launch
                            </Button>
                        )}
                        {slice.canModify && (
                            <IconButton
                                variant="tertiary"
                                icon={UsersIcon}
                                onPress={() => {
                                    setPermissionsOpen(true);
                                }}
                            />
                        )}
                    </XStack>
                </XStack>
                {slice.description.length > 0 && (
                    <BodyMediumRegular color="$gray1000">
                        {slice.description}
                    </BodyMediumRegular>
                )}
            </YStack>

            <YStack gap="$2">
                <H6Emphasized>Frames</H6Emphasized>
                {frames.length > 0 ? (
                    <RowList>
                        {frames.map((frame) => (
                            <Row
                                key={frame.id}
                                title={frameTitle(frame.type)}
                                subtitle={`${frame.id} · Version ${frame.version}`}
                                leftIcon={CodeIcon}
                                onPress={() => {
                                    router.navigate({
                                        pathname:
                                            '/fruit-slices/[id]/frames/[frameId]',
                                        params: {
                                            id: slice.id,
                                            frameId: frame.id,
                                        },
                                    });
                                }}
                                rightContent={
                                    frame.type === 'PAGE' ? (
                                        <IconButton
                                            variant="tertiary"
                                            icon={ExternalLinkIcon}
                                            onPress={(e) => {
                                                e.stopPropagation();
                                                router.navigate({
                                                    pathname:
                                                        '/fruit-slices/[id]',
                                                    params: {
                                                        id: slice.id,
                                                        frameId: frame.id,
                                                    },
                                                });
                                            }}
                                        />
                                    ) : undefined
                                }
                            />
                        ))}
                    </RowList>
                ) : (
                    <FootnoteRegular color="$gray1000">
                        This slice does not have an app frame.
                    </FootnoteRegular>
                )}
            </YStack>

            <YStack gap="$2">
                <H6Emphasized>Listeners</H6Emphasized>
                {listeners.length > 0 ? (
                    <RowList>
                        {listeners.map((listener) => (
                            <Row
                                key={listener.id}
                                title={listener.name}
                                subtitle={listener.description}
                                bottomContent={
                                    <FootnoteRegular color="$gray1000">
                                        {listener.enabled
                                            ? listener.eventTypes.join(', ')
                                            : 'Disabled'}
                                    </FootnoteRegular>
                                }
                                leftIcon={CodeIcon}
                                onPress={() => {
                                    router.navigate({
                                        pathname:
                                            '/fruit-slices/[id]/listeners/[listenerId]',
                                        params: {
                                            id: slice.id,
                                            listenerId: listener.id,
                                        },
                                    });
                                }}
                            />
                        ))}
                    </RowList>
                ) : (
                    <FootnoteRegular color="$gray1000">
                        This slice does not have any listeners.
                    </FootnoteRegular>
                )}
            </YStack>

            {permissionsOpen && (
                <FruitSlicePermissionsModal
                    sliceId={slice.id}
                    sliceName={slice.name}
                    ownerId={slice.createdBy}
                    isOpen
                    onClose={() => {
                        setPermissionsOpen(false);
                    }}
                />
            )}
        </YStack>
    );
};

const frameTitle = (type: FruitSliceFrameType) => {
    switch (type) {
        case 'PAGE':
            return 'Page frame';
        case 'SIDEBAR':
            return 'Sidebar frame';
        case 'WIDGET':
            return 'Widget frame';
    }
};
